import { Grid2 as Grid, Stack } from "@mui/material"
import log from "loglevel"
import { NumberField } from "../../../components/Fields/NumberField"
import { useSnackbarStore } from "../../../components/Snackbar/Snackbar"
import { LabelAndText } from "../../../components/Typography/Typography"
import { FlightlineEventReview, ReviewNode } from "../../../models/MissionReview"
import { useMissionReviewStore } from "../../../zstore/MissionReviewStore"

interface ReviewEventCardNodesProps {
    startNodeName: number
    stopNodeName: number
    reviewCardIndex: number
}

export const ReviewCardNodeNumberFields = ({
    startNodeName,
    stopNodeName,
    reviewCardIndex,
}: ReviewEventCardNodesProps) => {
    const selectedReviews = useMissionReviewStore((state) => state.currentPartialLines) ?? []
    const updatePartialLines = useMissionReviewStore((state) => state.updatePartialLines) ?? []

    const openSnackbar = useSnackbarStore((state) => state.handleOpen)

    const currentCard = selectedReviews[reviewCardIndex]
    const currentNodeInfo = currentCard.node_info
    const { minNodeName, maxNodeName } = calculateMinMaxNodesInRange(
        currentCard,
        selectedReviews[reviewCardIndex - 1],
        selectedReviews[reviewCardIndex + 1],
    )

    const nodeEvents = currentNodeInfo?.node_events ?? []

    const firstNode = nodeEvents.find((node) => node.node_original_name === currentNodeInfo?.start_node_name)
    const lastNode = nodeEvents.find((node) => node.node_original_name === currentNodeInfo?.stop_node_name)

    const isFirstCard = reviewCardIndex === 0
    const isLastCard = reviewCardIndex === selectedReviews.length - 1

    const handleUpdateFirstNode = (newNodeName: number) => {
        /*
         * Requirements: only update
         * - previous LAST NODE (keep first two nodes untouched - restricted)
         * - current FIRST NODE (keep the last node of the current card untouched)
         * this will prevent the current card's last node and previous card's first 2 nodes from overlapping
         */
        const newFirstNode = nodeEvents.find((n) => n.node_original_name === newNodeName)
        const updatedCurCard = updateReviewNode(currentCard, { firstNode: newFirstNode })

        const previousCard = selectedReviews[reviewCardIndex - 1]
        const newLastNodePrev = nodeEvents.find((node) => node.node_original_name === newNodeName - 1)
        const updatedPrevCard = updateReviewNode(previousCard, { lastNode: newLastNodePrev })

        if (!newFirstNode || !newLastNodePrev) {
            const errorMessage = `Unable to update first node with node name ${newNodeName}. The new first or last node doesn't exist.`
            log.error(errorMessage)
            openSnackbar({ message: errorMessage, severity: "error" })
        }
        const updatedSelectedReviews = selectedReviews.toSpliced(
            reviewCardIndex - 1,
            2,
            updatedPrevCard,
            updatedCurCard,
        )
        updatePartialLines(updatedSelectedReviews)
    }

    const handleUpdateLastNode = (newNodeName: number) => {
        /*
         * Requirements: only update
         * - current LAST NODE (cannot exceed current first node)
         * - next card's FIRST NODE (cannot exceed last 2 nodes)
         * this will prevent the current last node and current card's first node from overlapping
         */

        const newLastNode = nodeEvents.find((n) => n.node_original_name === newNodeName)
        const updatedCurCard = updateReviewNode(currentCard, { lastNode: newLastNode })

        const nextCard = selectedReviews[reviewCardIndex + 1]
        const newFirstNodeNext = nodeEvents.find((node) => node.node_original_name === newNodeName + 1)
        const updatedNextCard = updateReviewNode(nextCard, { firstNode: newFirstNodeNext })

        if (!newLastNode || !newFirstNodeNext) {
            const errorMessage = `Unable to update last node with node name ${newNodeName}. The new last or first node doesn't exist`
            log.error(errorMessage)
            openSnackbar({ message: errorMessage, severity: "error" })
        }

        const updatedSelectedReviews = selectedReviews.toSpliced(reviewCardIndex, 2, updatedCurCard, updatedNextCard)
        updatePartialLines(updatedSelectedReviews)
    }

    return (
        <Stack spacing={2} sx={{ pb: 2 }}>
            <Grid container size={{ sm: 12 }} spacing={2}>
                <Grid size={{ xs: 6 }}>
                    <NumberField
                        fullWidth
                        label={"First Node"}
                        disabled={isFirstCard}
                        value={startNodeName}
                        step={1}
                        min={minNodeName}
                        max={stopNodeName - 1}
                        onChange={(value) => {
                            if (typeof value === "number") {
                                handleUpdateFirstNode(value)
                            }
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <NumberField
                        label={"Last Node"}
                        fullWidth
                        disabled={isLastCard}
                        value={stopNodeName}
                        step={1}
                        min={startNodeName + 1}
                        max={maxNodeName}
                        onChange={(value) => {
                            if (typeof value === "number") {
                                handleUpdateLastNode(value)
                            }
                        }}
                    />
                </Grid>
            </Grid>
            {firstNode?.exposure_num != null && lastNode?.exposure_num != null && (
                <Grid container size={{ sm: 12 }} spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <LabelAndText label="Exposure ID">{firstNode?.exposure_num}</LabelAndText>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <LabelAndText label="Exposure ID">{lastNode?.exposure_num}</LabelAndText>
                    </Grid>
                </Grid>
            )}
        </Stack>
    )
}

const updateReviewNode = (
    review: FlightlineEventReview,
    { firstNode, lastNode }: { firstNode?: ReviewNode; lastNode?: ReviewNode },
): FlightlineEventReview => {
    const nodeInfo = review.node_info
    if (!nodeInfo) {
        throw new Error("Unable to update review node")
    }
    return {
        ...review,
        node_info: {
            ...nodeInfo,
            start_node_id: firstNode ? firstNode.id : nodeInfo.start_node_id,
            start_node_name: firstNode ? firstNode.node_original_name : nodeInfo.start_node_name,
            stop_node_id: lastNode ? lastNode.id : nodeInfo.stop_node_id,
            stop_node_name: lastNode ? lastNode.node_original_name : nodeInfo.stop_node_name,
        },
    }
}

/**
 * The min and max of nodes available
 * - previous card's nodes (excluding the first 2)
 * - the current nodes
 * - next card's nodes (excluding the last 2)
 */
const calculateMinMaxNodesInRange = (
    curReview: FlightlineEventReview,
    prevReview?: FlightlineEventReview,
    nextReview?: FlightlineEventReview,
) => {
    let minNodeName
    if (!prevReview && curReview.node_info) {
        minNodeName = curReview.node_info.start_node_name + 1
    } else if (prevReview?.node_info) {
        minNodeName = prevReview.node_info.start_node_name + 2
    }

    let maxNodeName
    if (!nextReview && curReview.node_info) {
        maxNodeName = curReview.node_info.stop_node_name - 1
    } else if (nextReview?.node_info) {
        maxNodeName = nextReview.node_info.stop_node_name - 2
    }

    if (!minNodeName || !maxNodeName) {
        throw new Error("Unable to calculate min or max of node range")
    }

    return { minNodeName, maxNodeName }
}
